
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, BarChart2, ArrowUpDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const TremendousRewardSalesReport = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('daily');
  const [reportData, setReportData] = useState([]);
  const [dateRange, setDateRange] = useState({ 
    start: new Date(new Date().setDate(new Date().getDate() - 30)), 
    end: new Date() 
  });

  useEffect(() => {
    fetchReportData();
  }, [activeTab, dateRange]);

  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      // This would typically call a Supabase function to get real data
      // For now, we'll use mock data
      
      // Simulating API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock data with both order numbers
      const mockData = [
        { 
          date: '2025-04-01', 
          totalSales: 12, 
          totalRevenue: 1240, 
          adminCount: 3,
          requestOrderNumber: 'RO123456789', 
          fulfillmentOrderNumber: 'RSO987654321' 
        },
        { 
          date: '2025-04-02', 
          totalSales: 8, 
          totalRevenue: 820, 
          adminCount: 2,
          requestOrderNumber: 'RO234567890', 
          fulfillmentOrderNumber: 'RSO876543210' 
        },
        { 
          date: '2025-04-03', 
          totalSales: 15, 
          totalRevenue: 1550, 
          adminCount: 4,
          requestOrderNumber: 'RO345678901', 
          fulfillmentOrderNumber: 'RSO765432109' 
        },
        { 
          date: '2025-04-04', 
          totalSales: 10, 
          totalRevenue: 1050, 
          adminCount: 3,
          requestOrderNumber: 'RO456789012', 
          fulfillmentOrderNumber: 'RSO654321098' 
        },
        { 
          date: '2025-04-05', 
          totalSales: 14, 
          totalRevenue: 1420, 
          adminCount: 5,
          requestOrderNumber: 'RO567890123', 
          fulfillmentOrderNumber: 'RSO543210987' 
        },
      ];
      
      setReportData(mockData);
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast({
        title: "Error",
        description: "Failed to load report data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Calculate summary stats
  const totalSales = reportData.reduce((sum, item) => sum + item.totalSales, 0);
  const totalRevenue = reportData.reduce((sum, item) => sum + item.totalRevenue, 0);
  const averageSale = totalSales ? totalRevenue / totalSales : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-xl">Reward Sales Report</CardTitle>
              <CardDescription>
                Overview of all reward card sales across the platform
              </CardDescription>
            </div>
            <button 
              onClick={fetchReportData} 
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <RefreshCw className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{totalSales}</div>
                <p className="text-muted-foreground text-sm">Total Sales</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{formatCurrency(totalRevenue)}</div>
                <p className="text-muted-foreground text-sm">Total Revenue</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{formatCurrency(averageSale)}</div>
                <p className="text-muted-foreground text-sm">Average Sale</p>
              </CardContent>
            </Card>
          </div>

          {/* Report Tabs */}
          <Tabs 
            defaultValue={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <TabsList>
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>
            
            <TabsContent value="daily" className="space-y-4">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Request #
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fulfillment #
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sales
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Revenue
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Admins
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.map((day, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{day.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{day.requestOrderNumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{day.fulfillmentOrderNumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{day.totalSales}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(day.totalRevenue)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{day.adminCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="weekly">
              <div className="py-8 text-center text-gray-500">
                Weekly reports coming soon
              </div>
            </TabsContent>
            
            <TabsContent value="monthly">
              <div className="py-8 text-center text-gray-500">
                Monthly reports coming soon
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default TremendousRewardSalesReport;
